/**
 * Snack Media's ad wrapper (FFH-1910). Values come from Snack's implementation
 * doc for publisher 2762, linked on the epic.
 */

import { matchesSegment } from '@lib/checkout/entry-page-exclusions';

export const SNACK_SITE_NAME = 'fantasyfootballhub.co.uk';

export const ADLIGHTNING_SCRIPT_ID = 'snack-adlightning';
export const ADLIGHTNING_SCRIPT_URL =
  'https://snackmedia-ron-tagan.adlightning.com/op.js';

export const PUBSTACK_LOADER_SCRIPT_ID = 'snack-pubstack-loader';
export const PUBSTACK_LOADER_SCRIPT_URL =
  'https://boot.pbstck.com/v1/adm/ff68a2df-5199-4188-b1ce-00b2ddacd0cc';

/** The floor ad the wrapper fixes to the bottom of the viewport itself. */
export const SNACK_STICKY_FLOOR_ID = 'snack-sticky';

/** The wrapper's class for an ad slot once an ad has rendered in it. */
export const RENDERED_AD_CLASS = 'adm-ad-rendered';

/**
 * Holds the floor ad's height, or 0px, on <html>. The sticky floor rules in
 * styles/globals.css read it, so the two names must match.
 */
export const STICKY_FLOOR_HEIGHT_PROPERTY = '--snack-sticky-floor-height';

/**
 * Set on <html> on an ad-free page. The wrapper can't be unloaded, so a floor
 * ad from earlier in the visit would follow the visitor there. The floor rules
 * in styles/globals.css read it to hide the floor, so the two names must match.
 */
export const FLOOR_HIDDEN_ATTRIBUTE = 'data-snack-floor-hidden';

/**
 * Set on <html> while the ad slots hold their space open (FFH-3481). The slot
 * rules in styles/globals.css read it, so the two names must match.
 */
export const AD_SPACE_ATTRIBUTE = 'data-snack-ads';

/**
 * Remembers that this browser's visitor is a paying member, so the next page
 * load's check before first paint leaves the slots closed instead of opening
 * them and closing them again once the membership arrives.
 */
export const PAID_MEMBER_STORAGE_KEY = 'ffhub_snack_ads_paid_member';
export const PAID_MEMBER_STORAGE_VALUE = '1';

/**
 * The sign-up and checkout journey shows none of Snack's ads, so an ad can't
 * pull a visitor away just as they are about to pay.
 */
const AD_FREE_PATH_PREFIXES = [
  '/join',
  '/member-upgrade',
  '/checkout-stripe',
  '/thank-you',
  '/funnel-welcome',
  '/auth'
];

export const isAdFreePath = (pathname: string): boolean =>
  AD_FREE_PATH_PREFIXES.some((prefix) => matchesSegment(pathname, prefix));

/**
 * How much of the bottom of the viewport the floor ad covers (FFH-3482).
 *
 * The wrapper reserves the floor's full height before an ad arrives, and when
 * the visitor closes it the floor is hidden but keeps its rendered class, so
 * only a rendered ad with a height counts.
 */
export const getStickyFloorHeight = (floor: Element | null): number => {
  if (!floor?.classList.contains(RENDERED_AD_CLASS)) return 0;
  return Math.ceil(floor.getBoundingClientRect().height);
};

export interface SnackAdEligibility {
  /**
   * The `web-snack-advertising-is-live` PostHog flag: on in the staging
   * project, off in production until Snack sign off (FFH-3484).
   */
  isAdvertisingLive: boolean;
  isAppViewLoading: boolean;
  isAppView: boolean;
  isUserLoading: boolean;
  /**
   * The session check failed. A genuine sign-out has no error, so this means
   * we don't know who the visitor is, not that they're logged out.
   */
  hasAuthError: boolean;
  isLoggedIn: boolean;
  /**
   * ffh-backend's entitlements have arrived for the signed-in member, and are
   * not a degraded fallback.
   */
  hasLiveMembership: boolean;
  isPaidMember: boolean;
}

/**
 * Whether this visitor should get Snack's ad scripts: free members and
 * logged-out visitors on the website, never paying members or the app view.
 *
 * Every unknown fails closed, because ad code can't be unloaded once it has
 * run.
 */
export const shouldLoadSnackAds = ({
  isAdvertisingLive,
  isAppViewLoading,
  isAppView,
  isUserLoading,
  hasAuthError,
  isLoggedIn,
  hasLiveMembership,
  isPaidMember
}: SnackAdEligibility): boolean => {
  if (!isAdvertisingLive) return false;
  if (isAppViewLoading || isAppView) return false;
  if (isUserLoading || hasAuthError) return false;
  if (!isLoggedIn) return true;

  // The Auth0 session's membership claims can be as old as the login, so a
  // member who has upgraded since would read as free until ffh-backend's
  // entitlements arrive. Wait for them; if they never arrive, show no ads.
  return hasLiveMembership && !isPaidMember;
};

/**
 * A signed-in member whose live membership from ffh-backend says they pay. The
 * session's claims alone don't count, for the reason in shouldLoadSnackAds.
 */
export const isConfirmedPaidMember = ({
  isLoggedIn,
  hasLiveMembership,
  isPaidMember
}: SnackAdEligibility): boolean =>
  isLoggedIn && hasLiveMembership && isPaidMember;

export type AdSpaceChange = 'open' | 'close' | 'keep';

/**
 * What the ad slots' space should do now that more is known about the visitor
 * than the check before first paint could see (FFH-3481).
 *
 * That check decides from the flag, the app view and a remembered paid
 * membership, because the session only arrives later. So this opens the space
 * once the visitor is known to get ads, closes it only for reasons that check
 * couldn't see, and otherwise leaves it alone: closing it while the session or
 * the flags are still loading would make the page jump twice for every
 * logged-out reader.
 */
export const getAdSpaceChange = (
  eligibility: SnackAdEligibility,
  hasAdStackFailed: boolean
): AdSpaceChange => {
  if (hasAdStackFailed) return 'close';
  if (shouldLoadSnackAds(eligibility)) return 'open';
  if (
    eligibility.isAppView ||
    eligibility.hasAuthError ||
    isConfirmedPaidMember(eligibility)
  ) {
    return 'close';
  }
  return 'keep';
};
